<template>
  <div class="passage-analyzer">
    <v-card >
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon large class="mr-3">mdi-chart-bar</v-icon>
          <div>
            <h2 class="text-h5 font-weight-bold">Analyseur de Compteur de Passage</h2>
            <div class="text-subtitle-1">Analyse comparative des passages, tickets et leur rapport</div>
          </div>
        </div>
        
        
      </v-card-title>
    </v-card>

    <!-- Filtres (désactivés si pas de données) -->
    <v-card class="mb-6" :disabled="!hasData">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-filter</v-icon>
        Filtres
      </v-card-title>
      <v-card-text>
        <v-row style="margin-top: 10px;">
            <v-col >
                <v-select
                    v-model="selectedEtab"
                    :items="availableEtablissements"
                    multiple
                    label="Sélectionner un établissement"
                    density="compact" variant="outlined"
                    dense
                    hide-details
                    class="mr-3"
                    style="background: white;"
                    @update:model-value="loadData"
                ></v-select>
            </v-col>
            <v-col >
                <v-btn
                    :loading="loading"
                    color="white"
                    @click="loadData"
                    :disabled="!selectedEtab"
                >
                <v-icon left>mdi-refresh</v-icon>
                    Actualiser
            </v-btn>
            </v-col>
        </v-row>
        <v-row style="margin-top: -5px;">
          <v-col >
            <v-combobox
              v-model="filters.annee"
              density="compact" variant="outlined"
              :items="annees"
              label="Année"
              multiple
              chips
              clearable
              :readonly="!hasData"
              @update:model-value="updateMainChart"
            ></v-combobox>
          </v-col>
          
          <v-col >
            <v-combobox
              v-model="filters.mois"
              density="compact" variant="outlined"
              :items="mois"
              label="Mois"
              multiple
              chips
              clearable
              :readonly="!hasData"
              @update:model-value="updateMainChart"
            ></v-combobox>
          </v-col>
          
          <v-col >
            <v-combobox
                density="compact" variant="outlined"
              v-model="filters.jour"
              :items="jours"
              label="Jour"
              multiple
              chips
              clearable
              :readonly="!hasData"
              @update:model-value="updateMainChart"
            ></v-combobox>
          </v-col>
          </v-row>
          <v-row style="margin-top: -30px;">
          <v-col >
            <v-combobox
              density="compact" variant="outlined"
              v-model="filters.semaine"
              :items="semaines"
              label="Semaine"
              multiple
              chips
              clearable
              :readonly="!hasData"
              @update:model-value="updateMainChart"
            ></v-combobox>
          </v-col>
        </v-row>
        
        <v-row style="margin-top: -30px;">
          <v-col cols="12" class="d-flex align-center">
            <v-btn 
              color="error" 
              class="mr-3" 
              @click="resetFilters"
              :disabled="!hasData"
            >
              <v-icon left>mdi-refresh</v-icon>
              Réinitialiser les filtres
            </v-btn>
            
            <v-spacer></v-spacer>
            
            <v-chip v-if="hasData" color="info" class="ml-2">
              Données filtrées : {{ filteredData.length }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-subtitle class="d-flex align-center">
        <v-chip v-if="selectedEtab" color="primary" class="mr-2">
          <v-icon left small>mdi-office-building</v-icon>
          {{ selectedEtab }}
        </v-chip>
        <v-chip v-if="rawData.length > 0" color="primary">
          <v-icon left small>mdi-database</v-icon>
          {{ rawData.length }} enregistrements
        </v-chip>
      </v-card-subtitle>
    </v-card>

    <!-- Contrôles du graphique -->
    <v-card class="mb-6" :disabled="!hasData">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-chart-box</v-icon>
        Graphique Principal - Analyse Comparative
      </v-card-title>
      <v-card-text>
        <v-row style="margin-top: 10px;">
          <v-col cols="12" sm="6" md="3">
            <v-select
              density="compact" variant="outlined"
              v-model="chartGrouping"
              :items="groupingOptions"
              label="Regroupement"
              :disabled="!hasData"
              @update:model-value="updateMainChart"
            ></v-select>
          </v-col>
          
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="dataType"
              :items="dataTypes"
              label="Type de données"
              density="compact" variant="outlined"
              chips
              :disabled="!hasData"
              @update:model-value="updateMainChart"
            ></v-select>
          </v-col>
          
          <v-col cols="12" sm="6" md="3">
            <v-switch
              v-model="showDataLabels"
              label="Afficher les valeurs"
              color="primary"
              hide-details
              :disabled="!hasData"
              @update:model-value="updateMainChart"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Graphique principal -->
    <v-card class="mb-6" :disabled="!hasData">
      <v-card-text>
        <div v-if="!hasData" class="text-center py-12">
          <v-icon size="100" color="grey lighten-2">mdi-chart-line</v-icon>
          <h3 class="text-h6 mt-4 mb-2">Aucune donnée disponible</h3>
          <p class="text-body-1 text-grey">Sélectionnez un établissement pour charger les données</p>
        </div>
        
        <div v-else class="chart-container">
          <canvas ref="mainChart"></canvas>
        </div>
      </v-card-text>
    </v-card>

    <!-- Tableau des données -->
    <v-card :disabled="!hasData">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-table</v-icon>
          Données Filtrées ({{ filteredData.length }} enregistrements)
        </div>
        
        <v-btn 
          @click="toggleTable" 
          color="primary" 
          size="small"
          :disabled="!hasData"
        >
          <v-icon left>{{ showTable ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
          {{ showTable ? 'Masquer' : 'Afficher' }}
        </v-btn>
      </v-card-title>
      
      <v-expand-transition>
        <div v-if="showTable && hasData">
          <v-data-table
            :headers="headers"
            :items="filteredData"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:item.nbre_ticket="{ item }">
              <v-chip color="green" dark>
                {{ formatNumber(item.nbre_ticket) }}
              </v-chip>
            </template>
            
            <template v-slot:item.nbre_passage="{ item }">
              <v-chip color="blue" dark>
                {{ formatNumber(item.nbre_passage) }}
              </v-chip>
            </template>
            
            <template v-slot:item.ratio="{ item }">
              <v-chip :color="getRatioColor(calculateRatio(item))" dark>
                {{ calculateRatio(item).toFixed(2) }}%
              </v-chip>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-icon
                small
                class="mr-2"
                @click="showItemDetails(item)"
              >
                mdi-information
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Snackbar pour les messages -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">
          Fermer
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Dialogue de détails -->
    <v-dialog v-model="detailDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          Détails de l'enregistrement
        </v-card-title>
        <v-card-text>
          <v-list v-if="selectedItem">
            <v-list-item>
              <v-list-item-title>Année</v-list-item-title>
              <v-list-item-subtitle>{{ selectedItem.annee }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Mois</v-list-item-title>
              <v-list-item-subtitle>{{ selectedItem.mois }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Semaine</v-list-item-title>
              <v-list-item-subtitle>{{ selectedItem.semaine }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Jour</v-list-item-title>
              <v-list-item-subtitle>{{ selectedItem.jour }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Établissement</v-list-item-title>
              <v-list-item-subtitle>{{ selectedItem.etablissement }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Nombre de tickets</v-list-item-title>
              <v-list-item-subtitle class="text-green">{{ formatNumber(selectedItem.nbre_ticket) }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Nombre de passages</v-list-item-title>
              <v-list-item-subtitle class="text-blue">{{ formatNumber(selectedItem.nbre_passage) }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Ratio tickets/passages</v-list-item-title>
              <v-list-item-subtitle :class="getRatioTextColor(calculateRatio(selectedItem))">
                {{ calculateRatio(selectedItem).toFixed(2) }}%
              </v-list-item-subtitle>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-title>Date</v-list-item-title>
              <v-list-item-subtitle>{{ formatDate(selectedItem.date) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import store from '@/store'
import Chart from 'chart.js/auto'
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels'

// Enregistrer le plugin
Chart.register(ChartJSPluginDatalabels)

// Références
const mainChart = ref(null)
let mainChartInstance = null

// États
const rawData = ref([])
const selectedEtab = ref([])
const availableEtablissements = ref(['MN01','LI02', 'EN11', 'FT04', 'DK03', 'RO06', 'AR09','CO18', 'HB21', 'VX05', 'CA24', 'PR25', 'LO07','NP19','BR08','GE22', 'OO10'])
const loading = ref(false)

const filters = ref({
  annee: ['2025','2026'],
  mois: [],
  jour: [],
  semaine: []
})

const chartType = ref('bar')
const chartGrouping = ref('mois')
const dataType = ref(['ratio'])
const showDataLabels = ref(true)  // Mis à true par défaut
const showTable = ref(true)
const detailDialog = ref(false)
const selectedItem = ref(null)

const snackbar = ref({
  show: false,
  message: '',
  color: 'info'
})

// Options
const chartTypes = [
  { title: 'Barres verticales', value: 'bar' },
  { title: 'Barres horizontales', value: 'horizontalBar' },
  { title: 'Barres groupées', value: 'groupedBar' }
]

const groupingOptions = [
  { title: 'Par année', value: 'annee' },
  { title: 'Par mois', value: 'mois' },
  { title: 'Par jour', value: 'jour' },
  { title: 'Par semaine', value: 'semaine' },
  { title: 'Par etablissement', value: 'etablissement' }
]

const dataTypes = [
  { title: 'Passages', value: 'passages' },
  { title: 'Tickets', value: 'tickets' },
  { title: 'Taux', value: 'ratio' }
]

const tokens = ref({ 
  'MN01':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzA4MTg4OTcsImV4cCI6MTc3MDgyMjQ5N30.lm7OqDOgknI65mMeTdzpMEhC05cIfiOnDmcm7hLc9bA' ,
  'LI02':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzA1NjQxOTIsImV4cCI6MTc3MDU2Nzc5Mn0.7RM0LRdlqACT9K7u2RpkzHbS_ouBZPIVPivzGnz7LPs' ,
  'DK03':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzAzOTU4NDMsImV4cCI6MTc3MDM5OTQ0M30._F5aFnGQ6Y2-IhqA7X7TDY86q-XnVEkYJ9WMvVgM6Rw' ,
  'FT04':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzAyMTIxOTAsImV4cCI6MTc3MDIxNTc5MH0.ZEGuOAxNE-mmugvtWCvYaFTslSFbofONhvukI6x54t0' ,
  'RO06':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6ImhiMjEiLCJpYXQiOjE3NzAwNDUxNTcsImV4cCI6MTc3MDA0ODc1N30.xM1P9oLJrS-FfEci54y3PCufDqQ9AU4fy46vmWNLRPc' ,
  'AR09':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzA0NTk2NjEsImV4cCI6MTc3MDQ2MzI2MX0.YuPhSfSpxdOOzlzaTOHbPVgQgheauW8eVEbFsrMLU74' ,
  'EN11':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3Njg5MjM1MTUsImV4cCI6MTc2ODkyNzExNX0.IPGT8P7VaT_YubApidLy34LJ9v0PMWzSuT2Z0i-68uo' ,
  'CO18':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzA4MjA1NTksImV4cCI6MTc3MDgyNDE1OX0.g2-8sLuIBtmBGdB6gyhSE7fzxQZ-pyILZHnEus99m48' ,
  'HB21':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzA4MDkwNjMsImV4cCI6MTc3MDgxMjY2M30.Yy_lHIasCet3Z9XLCe3evBrJGyiSmGJirIHHjQ0zJ18' ,
  'VX05':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NjgzMTkyMzEsImV4cCI6MTc2ODMyMjgzMX0.INQ-ZTSgttq5hBU49eok7EQgRwdoaLSnHsKyoeXd1BY' ,
  'CA24':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6InJvMDYiLCJpYXQiOjE3NzAyODMxMTIsImV4cCI6MTc3MDI4NjcxMn0.yWTApANjs9ScS3Yuu9idHoESREKGnAQ9ocENUzVSeZ4' ,
  'PR25':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6ImFyMDkiLCJpYXQiOjE3Njk3ODM0NzksImV4cCI6MTc2OTc4NzA3OX0.ukOH-mVl-JxaomdKU3FKUya5viIagcq4bqpktu1-1KY' ,
  'LO07':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6InJvMDYiLCJpYXQiOjE3Njk4NTMzMTksImV4cCI6MTc2OTg1NjkxOX0.p60qXSv2ktEOnV9wf2S7gvHFu5mpxYQ1AYO4oHYLuV4' ,
  'BR08':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzAxMzA4NTUsImV4cCI6MTc3MDEzNDQ1NX0.bNUgXlg1TdDiM8w9sb8MZOFtmt_bYeQyHec-WUIBCfU' ,
  'OO10':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6ImVuMTEiLCJpYXQiOjE3Njk3NjkyMjcsImV4cCI6MTc2OTc3MjgyN30.PB7lZ6EaKPgQaaE_-vXJxfCrQtrb-UeoLBKm3h65mFY' ,
  'NP19':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6ImNvMTgiLCJpYXQiOjE3NzAxMTE5ODIsImV4cCI6MTc3MDExNTU4Mn0.OAkybQjVvzG4xsXdm_UO82BqG39DTtB7VjopWiMVMew' ,
  'GE22':'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Im1uMDEiLCJpYXQiOjE3NzAxOTU5MDYsImV4cCI6MTc3MDE5OTUwNn0.mVU8RGnJ8CAzm331bhWAp886dIjfsoyc1EKcFIAniA4' 
})

// Headers pour le tableau
const headers = [
  { title: 'Année', key: 'annee', sortable: true },
  { title: 'Mois', key: 'mois', sortable: true },
  { title: 'Semaine', key: 'semaine', sortable: true },
  { title: 'Jour', key: 'jour', sortable: true },
  { title: 'Établissement', key: 'etablissement', sortable: true },
  { title: 'Tickets', key: 'nbre_ticket', sortable: true },
  { title: 'Passages', key: 'nbre_passage', sortable: true },
  { title: 'Taux (%)', key: 'ratio', sortable: false },
  { title: 'Date', key: 'date', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

// Computed properties
const hasData = computed(() => rawData.value.length > 0)

const annees = computed(() => {
  return [...new Set(rawData.value.map(item => item.annee))].sort()
})

const mois = computed(() => {
  const moisOrder = [
    'JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
    'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'
  ]
  const moisSet = new Set(rawData.value.map(item => item.mois))
  return moisOrder.filter(mois => moisSet.has(mois))
})

const jours = computed(() => {
  const joursOrder = [
    'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'
  ]
  const joursSet = new Set(rawData.value.map(item => item.jour))
  return joursOrder.filter(jour => joursSet.has(jour))
})

const semaines = computed(() => {
  return [...new Set(rawData.value.map(item => item.semaine))].sort((a, b) => a - b)
})

const filteredData = computed(() => {
  if (!hasData.value) return []
  
  return rawData.value.filter(item => {
    if (filters.value.annee.length > 0 && !filters.value.annee.includes(item.annee)) {
      return false
    }
    
    if (filters.value.mois.length > 0 && !filters.value.mois.includes(item.mois)) {
      return false
    }
    
    if (filters.value.jour.length > 0 && !filters.value.jour.includes(item.jour)) {
      return false
    }
    
    if (filters.value.semaine.length > 0 && !filters.value.semaine.includes(item.semaine)) {
      return false
    }
    
    if (selectedEtab.value.length > 0 && !selectedEtab.value.includes(item.etablissement)) {
      return false
    }
    
    return true
  })
})

// Fonction API
const getCrosspoint = async (etab) => {
  loading.value = true
  const url = `https://my.edisac.com/api/statistics/crosspoint?etabissement=${etab}`
  
  try {
    const config = {
      method: 'get',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${store.state.user.token}`   // ⬅️ Ajout du token
      },
      url: url
    }
    
    const response = await axios.request(config)
    
    if (response.data && response.data.stats) {
      rawData.value = response.data.stats.map(el => ({...el, date: el.date.split('T')[0]}))
      showSnackbar('Données chargées avec succès', 'success')
      updateMainChart()
    } else {
      rawData.value = []
      showSnackbar('Aucune donnée disponible pour cet établissement', 'warning')
    }
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error)
    showSnackbar('Erreur lors du chargement des données', 'error')
    rawData.value = []
  } finally {
    loading.value = false
  }
}

// Méthodes
const loadData = () => {
  if (selectedEtab.value && selectedEtab.value.length > 0) {
    // Si plusieurs établissements, joindre avec des virgules
    const etabParam = selectedEtab.value.join(',')
    getCrosspoint(etabParam)
  }
}

const resetFilters = () => {
  filters.value = {
    annee: ['2025', '2026'],
    mois: [],
    jour: [],
    semaine: []
  }
  updateMainChart()
}

const updateMainChart = () => {
  if (!hasData.value || !mainChart.value) return
  
  if (mainChartInstance) {
    mainChartInstance.destroy()
  }
  
  const ctx = mainChart.value.getContext('2d')
  
  // Préparer les données selon le regroupement choisi
  const groupedData = {}
  
  filteredData.value.forEach(item => {
    const groupKey = item[chartGrouping.value]
    const year = item.annee
    const compositeKey = `${year}-${groupKey}`
    
    if (!groupedData[compositeKey]) {
      groupedData[compositeKey] = {
        year: year,
        group: groupKey,
        passages: 0,
        tickets: 0,
        count: 0
      }
    }
    
    groupedData[compositeKey].passages += item.nbre_passage
    groupedData[compositeKey].tickets += item.nbre_ticket
    groupedData[compositeKey].count += 1
  })
  
  Object.keys(groupedData).forEach(key => {
    const group = groupedData[key]
    group.ratio = group.passages > 0 ? (group.tickets / group.passages) * 100 : 0
  })
  
  const allGroups = [...new Set(Object.values(groupedData).map(g => g.group))]
  
  const sortedGroups = allGroups.sort((a, b) => {
    const grouping = chartGrouping.value
    
    switch (grouping) {
      case 'mois':
        return mois.value.indexOf(a.toUpperCase()) - mois.value.indexOf(b.toUpperCase())
      case 'semaine':
        if (typeof a === 'string' && a.startsWith('S') && b.startsWith('S')) {
          return parseInt(a.substring(1)) - parseInt(b.substring(1))
        }
        return Number(a) - Number(b)
      case 'jour':
        if (jours.value.includes(a) && jours.value.includes(b)) {
          return jours.value.indexOf(a) - jours.value.indexOf(b)
        }
        return new Date(a) - new Date(b)
      default:
        return a.toString().localeCompare(b.toString())
    }
  })
  
  const allYears = [...new Set(Object.values(groupedData).map(g => g.year))]
  const labels = sortedGroups
  const datasets = []
  
  allYears.sort().forEach(year => {
    const yearData = {
      passages: [],
      tickets: [],
      ratio: []
    }
    
    sortedGroups.forEach(group => {
      const compositeKey = `${year}-${group}`
      const data = groupedData[compositeKey]
      
      if (data) {
        yearData.passages.push(data.passages)
        yearData.tickets.push(data.tickets)
        yearData.ratio.push(data.ratio)
      } else {
        yearData.passages.push(0)
        yearData.tickets.push(0)
        yearData.ratio.push(0)
      }
    })
    
    const yearColors = {
      '2023': { bg: 'rgba(153, 102, 255, 0.7)', border: 'rgb(153, 102, 255)' },
      '2024': { bg: 'rgba(54, 162, 235, 0.7)', border: 'rgb(54, 162, 235)' },
      '2025': { bg: 'rgba(75, 192, 192, 0.7)', border: 'rgb(75, 192, 192)' },
      '2026': { bg: 'rgba(255, 99, 132, 0.7)', border: 'rgb(255, 99, 132)' }
    }
    
    const color = yearColors[year] || { 
      bg: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.7)`, 
      border: `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)})` 
    }
    
    if (dataType.value.includes('passages')) {
      datasets.push({
        label: `Passages ${year}`,
        data: yearData.passages,
        backgroundColor: color.bg,
        borderColor: color.border,
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: 'y'
      })
    }
    
    if (dataType.value.includes('tickets')) {
      datasets.push({
        label: `Tickets ${year}`,
        data: yearData.tickets,
        backgroundColor: color.bg.replace('0.7', '0.5'),
        borderColor: color.border,
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: 'y'
      })
    }
    
    if (dataType.value.includes('ratio')) {
      datasets.push({
        label: `Taux ${year} (%)`,
        data: yearData.ratio,
        backgroundColor: color.bg.replace('0.7', '0.3'),
        borderColor: color.border,
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: 'y1',
        type: chartType.value === 'line' ? 'line' : 'bar'
      })
    }
  })
  
  const scales = {
    x: {
      title: {
        display: true,
        text: getGroupingLabel(chartGrouping.value),
        font: { size: 14, weight: 'bold' }
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      title: {
        display: true,
        font: { size: 14, weight: 'bold' }
      },
      ticks: {
        callback: function(value) {
          return formatNumber(value)
        }
      },
      ...(allYears.length > 1 && chartType.value === 'bar' && { grouped: true })
    }
  }
  
  if (dataType.value.includes('ratio')) {
    scales.y1 = {
      type: 'linear',
      display: true,
      position: 'right',
      title: {
        display: true,
        text: 'Taux (%)',
        font: { size: 14, weight: 'bold' }
      },
      grid: { drawOnChartArea: false },
      ticks: {
        callback: function(value) {
          return value.toFixed(1) + '%'
        }
      }
    }
  }
  
  // Configuration du graphique avec les valeurs À L'INTÉRIEUR des barres
  const chartConfig = {
    type: chartType.value === 'groupedBar' ? 'bar' : 
           chartType.value === 'stackedBar' ? 'bar' : 
           chartType.value,
    data: {
      labels: labels,
      datasets: datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 14 } }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label.includes('Taux') || label.includes('Ratio')) {
                return `${label}: ${context.raw.toFixed(2)}%`
              }
              return `${label}: ${formatNumber(context.raw)}`
            }
          }
        },
        // Configuration des valeurs à l'intérieur des barres
        datalabels: {
          display: showDataLabels.value,
          color: 'white',
          anchor: 'center',
          align: 'center',
          offset: 0,
          formatter: function(value, context) {
            if (value === 0 || value === null) return ''
            
            if (context.dataset.label.includes('Taux') || context.dataset.label.includes('Ratio')) {
              return value.toFixed(1) + '%'
            }
            return formatNumber(value)
          },
          font: {
            weight: 'bold',
            size: 11
          },
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 4,
          padding: {
            left: 4,
            right: 4,
            top: 2,
            bottom: 2
          }
        }
      },
      scales: scales
    }
  }
  
  if (chartType.value === 'stackedBar') {
    chartConfig.options.scales.x.stacked = true
    chartConfig.options.scales.y.stacked = true
  }
  
  mainChartInstance = new Chart(ctx, chartConfig)
}

const getGroupingLabel = (grouping) => {
  const labels = {
    'mois': 'Mois',
    'semaine': 'Semaine',
    'jour': 'Jour',
    'etablissement': 'Établissement',
    'annee': 'Année'
  }
  return labels[grouping] || grouping
}

const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(num)
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR')
}

const calculateRatio = (item) => {
  if (!item || item.nbre_passage === 0) return 0
  return (item.nbre_ticket / item.nbre_passage) * 100
}

const getRatioColor = (ratio) => {
  if (ratio > 50) return 'red'
  if (ratio > 20) return 'orange'
  if (ratio > 5) return 'yellow'
  return 'green'
}

const getRatioTextColor = (ratio) => {
  if (ratio > 50) return 'text-red'
  if (ratio > 20) return 'text-orange'
  if (ratio > 5) return 'text-yellow'
  return 'text-green'
}

const showItemDetails = (item) => {
  selectedItem.value = item
  detailDialog.value = true
}

const toggleTable = () => {
  showTable.value = !showTable.value
}

const showSnackbar = (message, color) => {
  snackbar.value = {
    show: true,
    message,
    color
  }
}

import { useRoute, onBeforeRouteUpdate } from 'vue-router'
const route = useRoute()
import router from '@/router'

let etab = ref([])
let type = ref([])
let currentUrl = ref()

function getLastWeekAndMonth() {
  const maintenant = new Date()
  const ilYa7Jours = new Date(maintenant)
  ilYa7Jours.setDate(maintenant.getDate() - 7)
  
  const date = new Date(ilYa7Jours)
  date.setDate(date.getDate() + 4 - (date.getDay() || 7))
  const semainePassee = Math.ceil(((date - new Date(date.getFullYear(), 0, 1)) / 86400000 + 1) / 7)
  
  const moisDernier = new Date(maintenant)
  moisDernier.setMonth(maintenant.getMonth() - 1)
  const nomMois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][moisDernier.getMonth()]
  
  return { semainePassee, moisDernier: nomMois }
}

const loadParams = () => {
  if (['A', 'D'].find(el => el == store.state.user.groupe) ||
     (route.query.etab && route.query.token && tokens.value[route.query.etab] == route.query.token)) {
    try {
      etab.value = []
      etab.value.push(route.query.etab.toUpperCase())
      type.value = route.query.type
      currentUrl.value = window.location.href
      
      switch (route.query.type) {
        case 'heb':
          filters.value.semaine = []
          filters.value.semaine.push(getLastWeekAndMonth().semainePassee.toString())
          chartGrouping.value = 'jour'
          break
        case 'mens':
          filters.value.mois = []
          filters.value.mois.push(getLastWeekAndMonth().moisDernier.toUpperCase())
          chartGrouping.value = 'semaine'
          break
        default:
          // chartGrouping.value='etab'
      }
      
      store.commit('addUser', { groupe: 'N' })
      availableEtablissements.value = etab.value
      selectedEtab.value = [route.query.etab.toUpperCase()]
      loadData()
      updateMainChart()
    } catch (err) {
      if (!['A', 'D'].find(el => el == store.state.user.groupe)) {
        router.replace({ path: '/login' })
      }
    }
  } else {
    router.replace({ path: '/login' })
  }
}

onBeforeRouteUpdate((to, from) => {
  loadParams()
})

onMounted(() => {
  if (availableEtablissements.value.length > 0) {
    selectedEtab.value = [availableEtablissements.value[0]]
    loadData()
    loadParams()
  }
  
  window.addEventListener('resize', updateMainChart)
})

onUnmounted(() => {
  if (mainChartInstance) {
    mainChartInstance.destroy()
  }
  window.removeEventListener('resize', updateMainChart)
})
</script>

<style scoped>
.passage-analyzer {
  padding: 20px;
  min-width: 900px; 
  width: 60%;
  margin: 0 auto;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.chart-container {
  position: relative;
  height: 500px;
  width: 100%;
  margin: 0 auto;
}

.v-card {
  border-radius: 12px;
  overflow: hidden;
}

.v-card-title {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.text-green {
  color: #4caf50 !important;
}

.text-blue {
  color: #2196f3 !important;
}

.text-red {
  color: #f44336 !important;
}

.text-orange {
  color: #ff9800 !important;
}

.text-yellow {
  color: #ffc107 !important;
}

@media (max-width: 960px) {
  .passage-analyzer {
    padding: 10px;
  }
  
  .chart-container {
    height: 400px;
  }
  
  .v-card-title {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 600px) {
  .chart-container {
    height: 300px;
  }
}
</style>